import { ErrorMessage, Field, Form, Formik } from 'formik';
import React, { useEffect, useState } from 'react';
import * as Yup from 'yup';
import ErrorMessageComponent from '../components/ErrorMessage';
import LoadingSpinner from '../components/LoadingSpinner';
import MobileModal from '../components/MobileModal';
import { PermissionButton } from '../components/PermissionButton';
import ResponsiveChart, { METRIC_COLOR_MAP } from '../components/ResponsiveChart';
import { PageHeader } from '../components/ui';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';
import { exportToExcel, ExcelColumn } from '../utils/exportToExcel';
import { toDateInputValue, todayDateInputValue } from '../utils/dateInput';

// Helper: display date-only with no timezone shifts
const formatDate = (dateString: string | undefined | null): string => {
  if (!dateString) return 'N/A';
  const raw = String(dateString);
  const dateOnly = raw.includes('T') ? raw.split('T')[0] : raw;
  // Prefer returning the exact YYYY-MM-DD string to avoid TZ conversions
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateOnly)) return dateOnly;
  return dateOnly || 'N/A';
};

interface Vineyard {
  id: number;
  name: string;
  location: string;
  planted_variety: string;
}

interface VineyardMaintenanceRecord {
  id: number;
  vineyard_id: number;
  measurement_date: string;
  brix: string;
  baume?: string; // Add Baumé field
  ph: string;
  ta?: string;
  sg?: string;
  grape_color?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
  vineyard?: Vineyard;
}

interface VineyardMaintenanceFormValues {
  vineyard_id: number;
  measurement_date: string;
  brix: string;
  baume: string; // Add Baumé field
  ph: string;
  ta?: string;
  sg?: string;
  grape_color?: string;
  notes?: string;
}

const VineyardMaintenanceSchema = Yup.object().shape({
  vineyard_id: Yup.number(),
  measurement_date: Yup.date().required('Reading date is required'),
  brix: Yup.number().transform((value, originalValue) =>
    originalValue === '' ? undefined : value
  ),
  baume: Yup.number().transform((value, originalValue) =>
    originalValue === '' ? undefined : value
  ),
  ph: Yup.number().transform((value, originalValue) =>
    originalValue === '' ? undefined : value
  ),
  ta: Yup.number().transform((value, originalValue) =>
    originalValue === '' ? undefined : value
  ),
  sg: Yup.number().transform((value, originalValue) =>
    originalValue === '' ? undefined : value
  ),
  grape_color: Yup.string().max(100),
  notes: Yup.string()
});

const VineyardMaintenance: React.FC<{ tabVisible?: boolean }> = ({ tabVisible = true }) => {
  const [maintenanceRecords, setMaintenanceRecords] = useState<VineyardMaintenanceRecord[]>([]);
  const [vineyards, setVineyards] = useState<Vineyard[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<any>(null);
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingRecord, setEditingRecord] = useState<VineyardMaintenanceRecord | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [selectedVineyardId, setSelectedVineyardId] = useState<number | null>(null);
  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);
  const [visibleMetrics, setVisibleMetrics] = useState<string[]>(['brix', 'baume', 'ph']);
  const [isChartOpen, setIsChartOpen] = useState<boolean>(true);
  const [lockedField, setLockedField] = useState<'brix' | 'baume' | null>(null);
  const { user } = useAuth();
  const isAdmin = (user?.Role?.name || (user as any)?.role?.name || '').toLowerCase() === 'admin';

  // Function to force a data refresh
  const refreshData = () => {
    setRefreshTrigger(prev => prev + 1); // Increment to trigger useEffect
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        console.log('Fetching maintenance records and vineyards...');

        // Using the exact URLs from the server responses.json
        const [maintenanceRes, vineyardsRes] = await Promise.all([
          api.get('/api/vineyard-maintenance'),
          api.get('/api/vineyard')
        ]);

        // Add detailed debugging
        console.log('API call complete...');
        console.log('Vineyard Maintenance API response status:', maintenanceRes.status);
        console.log('Vineyard API response status:', vineyardsRes.status);

        // Ensure consistent data structure by manually formatting if needed
        const formattedMaintenanceRecords = maintenanceRes.data.map((record: any) => {
          // Check for both camelCase and snake_case field names
          const vineyardId = record.vineyard_id !== undefined
            ? Number(record.vineyard_id)
            : (record.vineyardId !== undefined ? Number(record.vineyardId) : null);

          // Map vineyard properly 
          const vineyard = record.vineyard ? {
            ...record.vineyard,
            planted_variety: record.vineyard.planted_variety || record.vineyard.plantedVariety || 'N/A'
          } : null;

          // Calculate Baumé from Brix if not present (Baumé = Brix / 1.8)
          const brixValue = record.brix ? parseFloat(record.brix) : null;
          const baumeValue = record.baume 
            ? record.baume 
            : (brixValue !== null ? (brixValue / 1.8).toFixed(1) : '');

          console.log('Record raw:', record);
          console.log('Extracted vineyard_id:', vineyardId, 'from fields:',
            'vineyard_id=', record.vineyard_id,
            'vineyardId=', record.vineyardId);

          return {
            ...record,
            vineyard_id: vineyardId, // Use the extracted ID
            measurement_date: record.measurement_date || record.measurementDate,
            baume: baumeValue, // Add calculated Baumé
            grape_color: record.grape_color || record.grapeColor,
            vineyard: vineyard
          };
        });

        console.log('Formatted maintenance records:', formattedMaintenanceRecords);

        // FALLBACK: If no records returned, create a dummy record for testing display
        const recordsToUse = formattedMaintenanceRecords;

        console.log('Final records to display:', recordsToUse);
        setMaintenanceRecords(recordsToUse);
        setVineyards(vineyardsRes.data);

        // Do not auto-select a vineyard; keep "All Vineyards" (null) unless user selects otherwise
      } catch (err: any) {
        console.error('Error fetching data:', err);
        setError(err);
      } finally {
        setLoading(false);
      }
    };

    if (tabVisible) fetchData();
  }, [refreshTrigger, tabVisible]);

  const openModal = (record: VineyardMaintenanceRecord | null = null) => {
    setEditingRecord(record);
    setIsModalOpen(true);
    // Reset locked field when opening modal
    setLockedField(null);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingRecord(null);
    setLockedField(null); // Reset locked field when closing modal
  };

  const handleDelete = async (id: number) => {
    if (!isAdmin) return;
    if (!window.confirm('Are you sure you want to delete this maintenance record?')) return;
    try {
      await api.delete(`/api/vineyard-maintenance/${id}`);
      setMaintenanceRecords(prev => prev.filter(r => r.id !== id));
      setSuccessMessage('Maintenance record deleted successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err: any) {
      setError(err);
    }
  };

  const handleSubmit = async (values: VineyardMaintenanceFormValues, { resetForm, setSubmitting }: any) => {
    try {
      // Convert all values to their proper types and verify none are undefined
      const formattedValues = {
        vineyard_id: Number(values.vineyard_id),
        measurement_date: values.measurement_date,
        brix: values.brix ? parseFloat(values.brix) : null,
        baume: values.baume ? parseFloat(values.baume) : null,
        ph: values.ph ? parseFloat(values.ph) : null,
        ta: values.ta ? parseFloat(values.ta) : null,
        sg: values.sg ? parseFloat(values.sg) : null,
        grape_color: values.grape_color || null,
        notes: values.notes || ''
      };

      // Validate required fields (brix and ph are optional)
      if (!formattedValues.vineyard_id || !formattedValues.measurement_date) {
        setError('Missing required fields: vineyard and measurement date.');
        setSubmitting(false);
        return;
      }

      // Convert to camelCase for API submission (this is what the server expects)
      const apiSubmissionData = {
        vineyardId: formattedValues.vineyard_id,      // Convert to camelCase
        measurementDate: formattedValues.measurement_date, // Convert to camelCase
        brix: formattedValues.brix,
        baume: formattedValues.baume,
        ph: formattedValues.ph,
        ta: formattedValues.ta,
        sg: formattedValues.sg,
        grapeColor: formattedValues.grape_color,
        notes: formattedValues.notes
      };

      // Log the values for debugging
      console.log('Original formatted values (snake_case):', formattedValues);
      console.log('API submission data (camelCase):', apiSubmissionData);

      if (editingRecord) {
        // Update existing record
        console.log(`Making PUT request to /api/vineyard-maintenance/${editingRecord.id}`);
        const response = await api.put(`/api/vineyard-maintenance/${editingRecord.id}`, apiSubmissionData);
        console.log('PUT response:', response);

        // Ensure consistent property names in the response data
        const brixValue = response.data.brix || editingRecord.brix;
        const baumeValue = response.data.baume || editingRecord.baume || 
          (brixValue ? (parseFloat(brixValue.toString()) / 1.8).toFixed(1) : undefined);
        
        const updatedRecord: VineyardMaintenanceRecord = {
          ...editingRecord,
          ...response.data,
          // Make sure all properties are in the expected format
          id: response.data.id,
          vineyard_id: response.data.vineyardId || response.data.vineyard_id || editingRecord.vineyard_id,
          measurement_date: response.data.measurementDate || response.data.measurement_date || editingRecord.measurement_date,
          brix: brixValue?.toString() || editingRecord.brix,
          baume: baumeValue,
          ph: response.data.ph?.toString() || editingRecord.ph,
          ta: response.data.ta?.toString() || editingRecord.ta,
          sg: response.data.sg?.toString() || editingRecord.sg,
          grape_color: response.data.grape_color || response.data.grapeColor || editingRecord.grape_color,
          notes: response.data.notes ?? editingRecord.notes,
          createdAt: response.data.createdAt || editingRecord.createdAt,
          updatedAt: response.data.updatedAt || new Date().toISOString(),
          // Preserve the vineyard relationship
          vineyard: response.data.vineyard || editingRecord.vineyard
        };

        // Update local state immediately for responsive UI
        setMaintenanceRecords(maintenanceRecords.map(r => (r.id === editingRecord.id ? updatedRecord : r)));
        setSuccessMessage('Maintenance record updated successfully!');

        // Also refresh data from server to ensure consistency
        refreshData();
      } else {
        // Create new record
        console.log('Making POST request to /api/vineyard-maintenance');
        try {
          const response = await api.post('/api/vineyard-maintenance', apiSubmissionData);
          console.log('POST response:', response);

          // Ensure the response data has consistent property names
          const brixValue = response.data.brix;
          const baumeValue = response.data.baume || 
            (brixValue ? (parseFloat(brixValue.toString()) / 1.8).toFixed(1) : undefined);
          
          const newRecord: VineyardMaintenanceRecord = {
            ...response.data,
            // Convert any camelCase to snake_case fields
            id: response.data.id,
            vineyard_id: response.data.vineyardId || response.data.vineyard_id,
            measurement_date: response.data.measurementDate || response.data.measurement_date,
            brix: brixValue?.toString(),
            baume: baumeValue,
            ph: response.data.ph?.toString(),
            ta: response.data.ta?.toString(),
            sg: response.data.sg?.toString(),
            grape_color: response.data.grape_color || response.data.grapeColor || '',
            notes: response.data.notes || '',
            createdAt: response.data.createdAt || new Date().toISOString(),
            updatedAt: response.data.updatedAt || new Date().toISOString(),
            vineyard: response.data.vineyard || undefined
          };

          // Update local state immediately for responsive UI
          setMaintenanceRecords(prevRecords => [...prevRecords, newRecord]);
          setSuccessMessage('Maintenance record created successfully!');

          // Also refresh data from server to ensure consistency
          refreshData();
        } catch (postErr: any) {
          console.error('POST request failed:', postErr);
          console.error('Error response:', postErr.response?.data);
          console.error('Status code:', postErr.response?.status);
          console.error('Headers:', postErr.response?.headers);
          throw postErr;
        }
      }
      resetForm();
      closeModal();

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage(null);
      }, 3000);
    } catch (err: any) {
      console.error('Error saving maintenance record:', err);
      console.error('Error status:', err.response?.status);
      console.error('Error data:', err.response?.data);
      setError(err);
    } finally {
      setSubmitting(false);
    }
  };

  const handleVineyardChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const value = e.target.value;
    console.log('Dropdown value selected:', value);

    // Handle the "All Vineyards" case
    if (value === "") {
      console.log('Setting selectedVineyardId to null (All Vineyards)');
      setSelectedVineyardId(null);
    } else {
      const numericValue = Number(value);
      console.log('Setting selectedVineyardId to:', numericValue);
      setSelectedVineyardId(numericValue);
    }
  };

  // Restore proper filtering functionality
  const filteredRecords = selectedVineyardId !== null
    ? maintenanceRecords.filter(record => {
      // Ensure both values are numbers for consistent comparison
      const recordVineyardId = Number(record.vineyard_id);
      const selectedId = Number(selectedVineyardId);
      const shouldKeep = recordVineyardId === selectedId;

      // Debug logging to help diagnose any issues
      console.log(`Filtering record ID ${record.id}: 
          Record vineyard_id=${recordVineyardId} (${typeof recordVineyardId}), 
          Selected ID=${selectedId} (${typeof selectedId}), 
          Keep=${shouldKeep}`);

      return shouldKeep;
    })
    : maintenanceRecords; // Show all if no vineyard selected (null)

  console.log("Filter status: Selected Vineyard ID:", selectedVineyardId,
    "Filtered Records:", filteredRecords.length,
    "Total Records:", maintenanceRecords.length);

  if (loading) {
    return <LoadingSpinner />;
  }

  // Export vineyard maintenance records (respecting current vineyard filter).
  const handleExportToExcel = () => {
    if (!filteredRecords.length) return;

    const columns: ExcelColumn<VineyardMaintenanceRecord>[] = [
      {
        header: 'Vineyard',
        accessor: (r) => r.vineyard?.name || 'Unknown Vineyard',
      },
      {
        header: 'Location',
        accessor: (r) => r.vineyard?.location || 'N/A',
      },
      {
        header: 'Variety',
        accessor: (r) => r.vineyard?.planted_variety || 'N/A',
      },
      {
        header: 'Date',
        accessor: (r) => r.measurement_date,
      },
      { header: 'Brix', accessor: (r) => r.brix || '' },
      { header: 'Baumé', accessor: (r) => r.baume || '' },
      { header: 'pH', accessor: (r) => r.ph || '' },
      { header: 'TA', accessor: (r) => r.ta || '' },
      { header: 'SG', accessor: (r) => r.sg || '' },
      { header: 'Grape Color', accessor: (r) => r.grape_color || '' },
    ];

    exportToExcel({
      fileName: `vineyard-maintenance-${new Date()
        .toISOString()
        .slice(0, 10)}.xlsx`,
      sheetName: 'VineyardMaintenance',
      rows: filteredRecords,
      columns,
    });
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Vineyard Maintenance"
        subtitle="Track measurements and maintenance activities for your vineyards."
        variant="compact"
        actions={(
        <div className="flex gap-3">
          <button
            type="button"
            onClick={handleExportToExcel}
            className="inline-flex items-center px-4 py-2 border border-monday-border text-sm font-medium rounded-md shadow-sm text-monday-text bg-white hover:bg-monday-hover"
          >
            Export to Excel
          </button>
          <PermissionButton
            resource="vineyardMaintenance"
            action="create"
            onClick={() => openModal()}
          >
            Add Maintenance Record
          </PermissionButton>
        </div>
        )}
      />

      {error && (
        <ErrorMessageComponent error={error} className="mt-4" />
      )}

      {successMessage && (
        <div className="mt-4 bg-green-100 border border-green-400 text-green-700 px-4 py-3 rounded relative" role="alert">
          <span className="block sm:inline">{successMessage}</span>
        </div>
      )}

      <div className="mt-4 mb-6">
        <label htmlFor="vineyardFilter" className="form-label">
          Filter by Vineyard
        </label>
        <select
          id="vineyardFilter"
          className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-monday-border focus:outline-none focus:shadow-[0_0_0_4px_rgba(180,35,58,0.16)] focus:border-primary-500 hover:border-neutral-300 sm:text-sm rounded-xl transition-all duration-150"
          value={selectedVineyardId || ''}
          onChange={handleVineyardChange}
        >
          <option value="">All Vineyards</option>
          {vineyards.map((vineyard) => (
            <option key={vineyard.id} value={vineyard.id}>
              {vineyard.name} - {vineyard.location}
            </option>
          ))}
        </select>
      </div>

      {/* Metrics selector and Time Series Chart */}
      <div className="mb-6">
        <div className="bg-white/95 rounded-xl shadow border border-monday-border-light p-4">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
            <div>
              <h3 className="text-lg font-medium text-monday-text">Measurements Over Time</h3>
              <p className="text-sm text-monday-text-muted">Timeline reflects current filters</p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setIsChartOpen(prev => !prev)}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text hover:bg-monday-hover"
                aria-expanded={isChartOpen}
                aria-controls="vineyard-chart-section"
              >
                {isChartOpen ? 'Collapse' : 'Expand'}
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {[
                { key: 'brix', label: 'Brix' },
                { key: 'baume', label: 'Baumé' },
                { key: 'ph', label: 'pH' },
                { key: 'ta', label: 'TA' },
                { key: 'sg', label: 'SG' },
              ].map((m) => (
                <button
                  key={m.key}
                  onClick={() =>
                    setVisibleMetrics((prev) =>
                      prev.includes(m.key)
                        ? prev.filter((k) => k !== m.key)
                        : [...prev, m.key]
                    )
                  }
                  className={`px-3 py-1 rounded-full text-xs font-medium border transition-colors ${visibleMetrics.includes(m.key)
                    ? 'bg-primary-50 text-primary-600 border-primary-200'
                    : 'bg-white text-monday-text border-monday-border hover:bg-monday-hover'
                    }`}
                >
                  {m.label}
                </button>
              ))}
              <button
                onClick={() => setVisibleMetrics([])}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text-muted hover:bg-monday-hover"
              >
                Hide all
              </button>
              <button
                onClick={() => setVisibleMetrics(['brix', 'baume', 'ph'])}
                className="px-3 py-1 rounded-full text-xs font-medium border border-monday-border text-monday-text-muted hover:bg-monday-hover"
              >
                Core
              </button>
            </div>
          </div>

          {isChartOpen && (() => {
            const records = filteredRecords
              .slice()
              .sort((a, b) => {
                const ad = (a.measurement_date || '').slice(0, 10);
                const bd = (b.measurement_date || '').slice(0, 10);
                return ad.localeCompare(bd);
              });
            const labels = records.map((r) => formatDate(r.measurement_date));

            const metricDefs: Record<string, { label: string; color: string; getter: (r: any) => number | null }> = {
              brix: { label: 'Brix', color: METRIC_COLOR_MAP.brix, getter: (r) => (r.brix !== undefined && r.brix !== null ? Number(r.brix) : null) },
              baume: { label: 'Baumé', color: METRIC_COLOR_MAP.baume, getter: (r) => (r.baume !== undefined && r.baume !== null ? Number(r.baume) : null) },
              ph: { label: 'pH', color: METRIC_COLOR_MAP.ph, getter: (r) => (r.ph !== undefined && r.ph !== null ? Number(r.ph) : null) },
              ta: { label: 'TA', color: METRIC_COLOR_MAP.ta, getter: (r) => (r.ta !== undefined && r.ta !== null ? Number(r.ta) : null) },
              sg: { label: 'SG', color: METRIC_COLOR_MAP.sg, getter: (r) => (r.sg !== undefined && r.sg !== null ? Number(r.sg) : null) },
            };

            const datasets = visibleMetrics
              .filter((k) => metricDefs[k])
              .map((k) => {
                const def = metricDefs[k];
                return {
                  label: def.label,
                  data: records.map((r) => def.getter(r)),
                  borderColor: def.color,
                  backgroundColor: def.color + '0D',
                } as any;
              });

            const nonEmptyDatasets = datasets.filter((ds: any) => Array.isArray(ds.data) && ds.data.some((v: any) => v !== null && Number.isFinite(Number(v))));

            return (
              <div className="mt-4" id="vineyard-chart-section">
                {nonEmptyDatasets.length > 0 ? (
                  <ResponsiveChart
                    type="line"
                    title={selectedVineyardId ? 'Vineyard Measurements' : 'All Measurements'}
                    data={{ labels, datasets: nonEmptyDatasets } as any}
                    height="360px"
                    variant="embedded"
                    instanceKey={`vine-${selectedVineyardId || 'all'}-${visibleMetrics.join(',')}-${records.length}`}
                  />
                ) : (
                  <div className="bg-white rounded-xl shadow border border-monday-border-light p-8 text-center">
                    <svg className="mx-auto h-12 w-12 text-monday-status-neutral mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                    </svg>
                    <p className="text-monday-text font-medium text-sm mb-1">No data to display</p>
                    <p className="text-monday-text-muted text-xs">Select metrics above or add records to see insights</p>
                  </div>
                )}
              </div>
            );
          })()}
        </div>
      </div>

      <div className="mt-8 flex flex-col">
        {/* Desktop Table View */}
        <div className="hidden md:block -my-2 -mx-4 overflow-x-auto sm:-mx-6 lg:-mx-8">
          <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
            <div className="overflow-hidden shadow ring-1 ring-black ring-opacity-5 md:rounded-lg">
              <table className="min-w-full divide-y divide-monday-border">
                <thead className="bg-monday-header">
                  <tr>
                    <th
                      scope="col"
                      className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-monday-text sm:pl-6"
                    >
                      Vineyard
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Location
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Variety
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Date
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Brix
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Baumé
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      pH
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      TA
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      SG
                    </th>
                    <th
                      scope="col"
                      className="px-3 py-3.5 text-left text-sm font-semibold text-monday-text"
                    >
                      Grape Color
                    </th>
                    <th
                      scope="col"
                      className="relative py-3.5 pl-3 pr-4 sm:pr-6"
                    >
                      <span className="sr-only">Actions</span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-monday-border-light bg-white">
                  {filteredRecords.length > 0 ? (
                    filteredRecords.map((record) => {
                      // Log individual record for debugging
                      console.log('Rendering record:', record);

                      return (
                        <tr key={record.id}>
                          <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-monday-text sm:pl-6">
                            {record.vineyard ? record.vineyard.name : 'Unknown Vineyard'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {record.vineyard?.location || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {record.vineyard?.planted_variety || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {formatDate(record.measurement_date)}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {record.brix || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {record.baume || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {record.ph || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {record.ta || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {record.sg || 'N/A'}
                          </td>
                          <td className="whitespace-nowrap px-3 py-4 text-sm text-monday-text-muted">
                            {record.grape_color || 'N/A'}
                          </td>
                          <td className="relative whitespace-nowrap py-4 pl-3 pr-4 text-right text-sm font-medium sm:pr-6 space-x-3">
                            <PermissionButton
                              resource="vineyardMaintenance"
                              action="update"
                              onClick={() => openModal(record)}
                              variant="secondary"
                              className="!px-2 !py-1 text-sm"
                            >
                              Edit
                            </PermissionButton>
                            {isAdmin && (
                              <button
                                onClick={() => handleDelete(record.id)}
                                className="inline-flex items-center rounded-md bg-red-50 px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-100 border border-red-200"
                              >
                                Delete
                              </button>
                            )}
                          </td>
                        </tr>
                      )
                    })
                  ) : (
                    <tr>
                      <td colSpan={10} className="py-4 text-center text-sm text-monday-text-muted">
                        No maintenance records found. Click "Add Maintenance Record" to create your first record.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* Mobile Card/List View */}
        <div className="md:hidden space-y-4">
          {filteredRecords.length > 0 ? (
            filteredRecords.map((record) => (
              <div key={record.id} className="bg-white/95 rounded-xl shadow border border-monday-border-light p-4 flex flex-col gap-2">
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-lg font-bold text-monday-text">{record.vineyard ? record.vineyard.name : 'Unknown Vineyard'}</div>
                    <div className="text-xs text-monday-text-muted">{record.vineyard?.location || 'N/A'}</div>
                    <div className="text-xs text-monday-text-muted">{record.vineyard?.planted_variety || 'N/A'}</div>
                  </div>
                  <div className="flex items-center gap-2">
                    <PermissionButton
                      resource="vineyardMaintenance"
                      action="update"
                      onClick={() => openModal(record)}
                      variant="secondary"
                      className="!ml-2 !px-3 !py-1 !text-sm"
                    >
                      Edit
                    </PermissionButton>
                    {isAdmin && (
                      <button
                        onClick={() => handleDelete(record.id)}
                        className="px-3 py-1 rounded-lg bg-red-50 text-red-700 text-sm font-medium hover:bg-red-100 border border-red-200"
                      >
                        Delete
                      </button>
                    )}
                  </div>
                </div>
                <div className="flex flex-wrap gap-4 mt-2">
                  <div>
                    <span className="block text-xs text-monday-text-muted">Date</span>
                    <span className="text-sm text-monday-text">{formatDate(record.measurement_date)}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">Brix</span>
                    <span className="text-sm text-monday-text">{record.brix || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">Baumé</span>
                    <span className="text-sm text-monday-text">{record.baume || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">pH</span>
                    <span className="text-sm text-monday-text">{record.ph || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">TA</span>
                    <span className="text-sm text-monday-text">{record.ta || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">SG</span>
                    <span className="text-sm text-monday-text">{record.sg || 'N/A'}</span>
                  </div>
                  <div>
                    <span className="block text-xs text-monday-text-muted">Grape Color</span>
                    <span className="text-sm text-monday-text">{record.grape_color || 'N/A'}</span>
                  </div>
                </div>
                {record.notes && (
                  <div className="mt-2">
                    <span className="block text-xs text-monday-text-muted">Notes</span>
                    <span className="text-sm text-monday-text">{record.notes}</span>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="bg-white/95 rounded-xl shadow border border-monday-border-light p-6 text-center text-monday-text-muted">
              No maintenance records found. Click "Add Maintenance Record" to create your first record.
            </div>
          )}
        </div>
      </div>

      <MobileModal isOpen={isModalOpen} onClose={closeModal} title={editingRecord ? 'Edit Maintenance Record' : 'Add New Maintenance Record'} size="full">
        <div className="mobile-form-container">
          <Formik
            initialValues={{
              vineyard_id: editingRecord
                ? editingRecord.vineyard_id
                : (selectedVineyardId !== null
                  ? selectedVineyardId
                  : (vineyards.length > 0 ? vineyards[0].id : 0)
                ),
              measurement_date: editingRecord?.measurement_date
                ? toDateInputValue(editingRecord.measurement_date)
                : todayDateInputValue(),
              brix: editingRecord && editingRecord.brix ? editingRecord.brix : '',
              baume: editingRecord && editingRecord.baume ? editingRecord.baume : '',
              ph: editingRecord && editingRecord.ph ? editingRecord.ph : '',
              ta: editingRecord && editingRecord.ta ? editingRecord.ta : '',
              sg: editingRecord && editingRecord.sg ? editingRecord.sg : '',
              grape_color: editingRecord?.grape_color || '',
              notes: editingRecord?.notes || ''
            }}
            validationSchema={VineyardMaintenanceSchema}
            onSubmit={handleSubmit}
            enableReinitialize
          >
            {({ isSubmitting, errors, touched, setFieldValue, values }) => {
              // Handle Brix change - calculate Baumé
              const handleBrixChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const brixValue = e.target.value;
                setFieldValue('brix', brixValue);
                
                if (brixValue && brixValue !== '') {
                  const brixNum = parseFloat(brixValue);
                  if (!isNaN(brixNum)) {
                    const baumeValue = (brixNum / 1.8).toFixed(1);
                    setFieldValue('baume', baumeValue);
                    setLockedField('baume');
                  }
                } else {
                  setLockedField(null);
                }
              };

              // Handle Baumé change - calculate Brix
              const handleBaumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                const baumeValue = e.target.value;
                setFieldValue('baume', baumeValue);
                
                if (baumeValue && baumeValue !== '') {
                  const baumeNum = parseFloat(baumeValue);
                  if (!isNaN(baumeNum)) {
                    const brixValue = (baumeNum * 1.8).toFixed(1);
                    setFieldValue('brix', brixValue);
                    setLockedField('brix');
                  }
                } else {
                  setLockedField(null);
                }
              };

              return (
                <Form className="space-y-4">
                  <div className="mb-4">
                    <label htmlFor="vineyard_id" className="form-label">Vineyard</label>
                    <Field as="select" name="vineyard_id" id="vineyard_id" className={`mobile-form-input ${errors.vineyard_id && touched.vineyard_id ? 'border-red-500' : ''}`}>
                      {vineyards.map(vineyard => (
                        <option key={vineyard.id} value={vineyard.id}>
                          {vineyard.name} - {vineyard.location}
                        </option>
                      ))}
                    </Field>
                    <ErrorMessage name="vineyard_id" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="measurement_date" className="form-label">Date</label>
                    <Field type="date" name="measurement_date" id="measurement_date" className={`mobile-form-input ${errors.measurement_date && touched.measurement_date ? 'border-red-500' : ''}`} />
                    <ErrorMessage name="measurement_date" component="div" className="text-red-600 text-sm mt-1" />
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="brix" className="form-label">
                        Brix {lockedField === 'brix' && <span className="text-xs text-monday-text-muted">(auto-calculated)</span>}
                      </label>
                      <input 
                        type="number" 
                        name="brix" 
                        id="brix" 
                        step="0.1" 
                        placeholder="e.g. 20.0" 
                        className={`mobile-form-input ${lockedField === 'brix' ? 'bg-monday-header' : ''}`}
                        value={values.brix}
                        onChange={handleBrixChange}
                        disabled={lockedField === 'brix'}
                      />
                      <ErrorMessage name="brix" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div>
                      <label htmlFor="baume" className="form-label">
                        Baumé {lockedField === 'baume' && <span className="text-xs text-monday-text-muted">(auto-calculated)</span>}
                      </label>
                      <input 
                        type="number" 
                        name="baume" 
                        id="baume" 
                        step="0.1" 
                        placeholder="e.g. 12.0" 
                        className={`mobile-form-input ${lockedField === 'baume' ? 'bg-monday-header' : ''}`}
                        value={values.baume}
                        onChange={handleBaumeChange}
                        disabled={lockedField === 'baume'}
                      />
                      <ErrorMessage name="baume" component="div" className="text-red-500 text-sm mt-1" />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="ph" className="form-label">pH</label>
                      <Field type="number" name="ph" id="ph" step="0.01" placeholder="e.g. 3.5" className="mobile-form-input" />
                      <ErrorMessage name="ph" component="div" className="text-red-500 text-sm mt-1" />
                    </div>

                    <div>
                      <label htmlFor="ta" className="form-label">TA</label>
                      <Field type="number" name="ta" id="ta" step="0.01" placeholder="e.g. 6.0" className="mobile-form-input" />
                      <ErrorMessage name="ta" component="div" className="text-red-500 text-sm mt-1" />
                    </div>
                  </div>

                  <div className="mb-4">
                    <label htmlFor="sg" className="form-label">SG</label>
                    <Field type="number" name="sg" id="sg" step="0.001" placeholder="e.g. 1.090" className="mobile-form-input" />
                    <ErrorMessage name="sg" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="grape_color" className="form-label">Grape Color</label>
                    <Field type="text" name="grape_color" id="grape_color" placeholder="e.g. Deep ruby, pale straw" className="mobile-form-input" />
                    <ErrorMessage name="grape_color" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="mb-4">
                    <label htmlFor="notes" className="form-label">Notes</label>
                    <Field as="textarea" name="notes" id="notes" rows={3} placeholder="e.g. Additional notes about measurements" className="mobile-form-input resize-none" />
                    <ErrorMessage name="notes" component="div" className="text-red-500 text-sm mt-1" />
                  </div>

                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse gap-3">
                    <button type="submit" disabled={isSubmitting} className="mobile-btn mobile-btn-primary w-full sm:w-auto">
                      {isSubmitting ? 'Saving...' : 'Save'}
                    </button>
                    <button type="button" onClick={closeModal} className="mobile-btn mobile-btn-secondary w-full sm:w-auto">
                      Cancel
                    </button>
                  </div>
                </Form>
              );
            }}
          </Formik>
        </div>
      </MobileModal>
    </div>
  );
};

export default VineyardMaintenance; 